import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Loader2, Truck, ChefHat, MapPin, CheckCircle2, Store, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { ordersApi, vendorAdminApi, driverApi } from '../services/api';
import { formatMoney } from '../lib/money';
/**
 * Pretty status label + color per order state. Kept in the component so the
 * mapping is co-located with the tiny UI it drives.
 */
const STATUS_META = {
  placed: { label: 'Placed', className: 'bg-slate-100 text-slate-700', Icon: Package },
  awaiting_vendor_confirmation: { label: 'Awaiting vendor', className: 'bg-amber-100 text-amber-800', Icon: Loader2 },
  payment_confirmed: { label: 'Payment confirmed', className: 'bg-emerald-50 text-emerald-800', Icon: CheckCircle2 },
  preparing: { label: 'Preparing', className: 'bg-orange-100 text-orange-800', Icon: ChefHat },
  ready: { label: 'Ready', className: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  ready_for_pickup: { label: 'Ready for pickup', className: 'bg-emerald-100 text-emerald-800', Icon: Store },
  accepted: { label: 'Driver accepted', className: 'bg-blue-50 text-blue-800', Icon: Truck },
  en_route_to_vendor: { label: 'En route to store', className: 'bg-blue-50 text-blue-800', Icon: Truck },
  arrived_at_vendor: { label: 'Driver at store', className: 'bg-indigo-50 text-indigo-800', Icon: MapPin },
  picked_up: { label: 'Picked up', className: 'bg-blue-100 text-blue-900', Icon: Truck },
  on_the_way: { label: 'On the way', className: 'bg-blue-100 text-blue-900', Icon: Truck },
  arrived_at_customer: { label: 'Arrived at you', className: 'bg-emerald-100 text-emerald-900', Icon: MapPin },
  delivered: { label: 'Delivered', className: 'bg-emerald-600 text-white', Icon: CheckCircle2 },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, className: 'bg-slate-100 text-slate-700', Icon: Package };
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${meta.className}`}
      data-testid={`status-pill-${status}`}
    >
      <Icon className={`h-3 w-3 ${status === 'awaiting_vendor_confirmation' ? 'animate-spin' : ''}`} />
      {meta.label}
    </span>
  );
};

/**
 * Return the next inline action the current role can perform on this order.
 *   { label, nextStatus, requiresPortal? }
 * If `requiresPortal` is true (POD photos / QR scan / etc.) we surface a
 * link into the portal instead of a fire-and-forget PATCH.
 */
const nextActionFor = (role, status, isShipper) => {
  // Shipper customers (courier / logistics / same-day) can drive the
  // vendor-side transitions on their own order.
  if (role === 'vendor' || (role === 'customer' && isShipper)) {
    if (status === 'placed') return { label: 'Confirm payment', nextStatus: 'payment_confirmed' };
    if (status === 'payment_confirmed') return { label: 'Start preparing', nextStatus: 'preparing' };
    if (status === 'preparing') return { label: 'Mark ready', nextStatus: 'ready' };
  }
  if (role === 'driver') {
    if (status === 'accepted') return { label: 'Start pickup', nextStatus: 'en_route_to_vendor' };
    if (status === 'en_route_to_vendor') return { label: 'Arrived at store', nextStatus: 'arrived_at_vendor' };
    if (status === 'arrived_at_vendor') return { label: 'Scan pickup QR', requiresPortal: true };
    if (status === 'picked_up') return { label: 'On the way', nextStatus: 'on_the_way' };
    if (status === 'on_the_way') return { label: 'Arrived at customer', nextStatus: 'arrived_at_customer' };
    if (status === 'arrived_at_customer') return { label: 'Upload proof + deliver', requiresPortal: true };
  }
  return null;
};

const advanceApi = async (order, nextStatus) => {
  if (order.role === 'vendor') return vendorAdminApi.updateOrderStatus(order.id, nextStatus);
  if (order.role === 'driver') return driverApi.updateOrderStatus(order.id, nextStatus);
  if (order.role === 'customer' && order.is_shipper) return ordersApi.shipperUpdateStatus(order.id, nextStatus);
  throw new Error('Only vendors / drivers / shippers can advance status');
};

const ActiveOrdersDropdownSection = ({ onNavigate }) => {
  const [state, setState] = useState({ loading: true, orders: [], error: null });
  const [busy, setBusy] = useState(null); // order.id currently mid-PATCH
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { orders } = await ordersApi.activeSummary(5);
      setState({ loading: false, orders: orders || [], error: null });
    } catch (e) {
      setState({ loading: false, orders: [], error: e?.response?.data?.detail || 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await load(); })();
    const t = setInterval(() => { if (alive) load(); }, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, [load]);

  const advance = async (order, action) => {
    if (action.requiresPortal) {
      const path = order.role === 'driver' ? '/driver/deliveries' : '/vendor-admin/orders';
      if (onNavigate) onNavigate();
      nav(path);
      return;
    }
    setBusy(order.id);
    try {
      await advanceApi(order, action.nextStatus);
      toast.success(`Order #${order.id} → ${STATUS_META[action.nextStatus]?.label || action.nextStatus}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const openRow = (o) => {
    const path = o.role === 'driver' ? '/driver/deliveries'
      : o.role === 'vendor' ? '/vendor-admin/orders'
      : `/track/${o.id}`;
    if (onNavigate) onNavigate();
    nav(path);
  };

  if (state.loading) {
    return (
      <div className="px-3 py-2 flex items-center gap-2 text-xs text-slate-500" data-testid="active-orders-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading active orders…
      </div>
    );
  }
  if (state.error) {
    return <div className="px-3 py-2 text-[11px] text-rose-600" data-testid="active-orders-error">{state.error}</div>;
  }
  if (!state.orders.length) {
    return <div className="px-3 py-2 text-[11px] text-slate-400" data-testid="active-orders-empty">No active orders right now.</div>;
  }

  return (
    <div className="max-h-[340px] overflow-y-auto" data-testid="active-orders-list">
      {state.orders.map((o) => {
        const action = nextActionFor(o.role, o.status, o.is_shipper);
        const isBusy = busy === o.id;
        return (
          <div
            key={o.id}
            className="px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
            data-testid={`active-order-row-${o.id}`}
          >
            <button
              onClick={() => openRow(o)}
              className="w-full text-left focus:outline-none"
              data-testid={`active-order-open-${o.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium text-slate-900 truncate">{o.vendor_name || 'Store'}</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">#{o.id}</div>
                </div>
                <StatusPill status={o.status} />
              </div>
              <div className="mt-1 text-[10px] text-slate-400 flex items-center gap-1.5">
                {o.role === 'driver' ? <><Truck className="h-3 w-3" /> You&rsquo;re the driver</>
                  : o.role === 'vendor' ? <><Store className="h-3 w-3" /> Store order</>
                  : o.is_shipper ? <><Package className="h-3 w-3" /> Your shipment</>
                  : <><Package className="h-3 w-3" /> Your order</>}
                {o.total != null && <> · {formatMoney(o.total, o.currency)}</>}
              </div>
            </button>
            {action && (
              <button
                onClick={(e) => { e.stopPropagation(); advance(o, action); }}
                disabled={isBusy}
                className={`mt-2 w-full inline-flex items-center justify-center gap-1 rounded-md ${action.requiresPortal ? 'bg-slate-800 hover:bg-slate-900' : 'bg-emerald-600 hover:bg-emerald-700'} text-white text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 disabled:opacity-60`}
                data-testid={`advance-status-${o.id}`}
              >
                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronRight className="h-3 w-3" />}
                {action.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ActiveOrdersDropdownSection;
export { StatusPill, STATUS_META, nextActionFor };
